import type {
  DatumAbstraction,
  WallDatumAbstraction,
  WallVerticalDatumAbstraction,
} from "building-types/datums/datumAbstractionTypes";
import type { DatumEntity } from "building-types/datums/datumEntityTypes";
import type { DatumUsage } from "building-types/datums/datumUsageTypes";
import { DatumHorizontalUsageType, DatumVerticalUsageType } from "building-types/datums/datumUsageTypes";
import type { FacadeSpanStratum, FacadeSweepStratum } from "building-types/facades/facadeTypes";
import type { Level, LevelAttributes } from "building-types/levels/levelTypes";
import type { WallSideDescriptor } from "building-types/walls/wallDesciptorTypes";
import type { RWTable, Table } from "common/data/table";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { ensureDefined } from "common/utils/assertOfType";
import { collectAllWallDatums, collectWallFacadeDatums } from "../../utils/facade";
import { createDatumAbstraction } from "./datumAbstractionFactory";
import { getDatumAbstractionWithFallback } from "./datumAbstractions";
import { defaultWallDatumAbstractions } from "./defaultDatumAbstractions";

class DatumAbstractionProviderBase {
  /**
   * @NOTE - some usages of the provider may need to mutate the table,
   * so it is a mutable clone of the original table.
   */
  private readonly mutableDatumAbstractions: RWTable<DatumAbstraction>;

  constructor(
    /**
     * Original datum abstractions table.
     * This table should not be stored in the provider, or
     * modified in any way. It is only used to collect the datum abstractions.
     */
    datumAbstractions: Table<DatumAbstraction>,
  ) {
    // copy the datumAbstractions to avoid mutating the original table
    this.mutableDatumAbstractions = { ...datumAbstractions };
  }

  public addDatumEntity(datumEntity: DatumEntity) {
    this.addDatumAbstraction(createDatumAbstraction(datumEntity));
  }

  public addDatumAbstraction(datumAbstraction: DatumAbstraction) {
    this.mutableDatumAbstractions[datumAbstraction.getId()] = datumAbstraction;
  }

  public getDatumAbstractions(): Table<DatumAbstraction> {
    return this.mutableDatumAbstractions;
  }
}

/**
 * The WallDatumAbstractionProvider is a lightweight wrapper around
 * the datumAbstraction table that prevents the need to prop drill contextual information
 * into the datumAbstraction resolution methods.
 */
export class WallDatumAbstractionProvider extends DatumAbstractionProviderBase {
  constructor(
    private readonly levels: Table<Level>,
    private readonly levelAttributes: Table<LevelAttributes>,
    datumAbstractions: Table<DatumAbstraction>,
  ) {
    super(datumAbstractions);
  }

  public getWallDatumAbstractions(): Table<WallDatumAbstraction> {
    return collectAllWallDatums(this.getDatumAbstractions());
  }

  public getWallFacadeDatumAbstractions(
    wallFacadeSpans: FacadeSpanStratum[],
    wallFacadeSweeps: FacadeSweepStratum[],
  ): Table<WallDatumAbstraction> {
    return collectWallFacadeDatums(this.getDatumAbstractions(), wallFacadeSpans, wallFacadeSweeps);
  }

  public getWallHorizontalDatumAbstraction(
    datumUsage: DatumUsage<DatumHorizontalUsageType>,
    wallSideHorizontalDatumUsage: "WALL_START" | "WALL_END",
  ) {
    return getDatumAbstractionWithFallback({
      datumAbstractions: this.getDatumAbstractions(),
      datumUsage,
      requestedUsageType: DatumHorizontalUsageType.WALL_HORIZONTAL,
      fallbackDatumAbstraction:
        wallSideHorizontalDatumUsage === "WALL_START"
          ? defaultWallDatumAbstractions.startDatum
          : defaultWallDatumAbstractions.endDatum,
    });
  }

  /**
   * @NOTE - this function will be made private in the future,
   * and all usage will go through the resolveWallVerticalDatumAbstractionRelativeToWallSide method
   */
  public getWallVerticalDatumAbstraction(
    datumUsage: DatumUsage<DatumVerticalUsageType>,
    wallSideVerticalDatumUsage: "WALL_TOP" | "WALL_BOTTOM",
    componentInstanceChain?: string[],
  ) {
    return getDatumAbstractionWithFallback({
      datumAbstractions: this.getDatumAbstractions(),
      datumUsage,
      requestedUsageType: DatumVerticalUsageType.WALL_VERTICAL,
      componentInstanceChain,
      fallbackDatumAbstraction:
        wallSideVerticalDatumUsage === "WALL_TOP"
          ? defaultWallDatumAbstractions.upperDatum
          : defaultWallDatumAbstractions.lowerDatum,
    });
  }

  /**
   * @deprecated - use resolveVerticalDatumRelativeToWallSide instead
   * This method only exists as an escape hatch for wall assembly layer overlap checking
   * and will be removed once we remove base_wall_bottom and base_wall_top
   */
  public resolveWallVerticalDatumAbstractionRelativeToWallSide(
    datumAbstraction: WallVerticalDatumAbstraction,
    wallSide: WallSideDescriptor,
    extraOffset = 0,
  ): LevelInches {
    const level = this.levels[wallSide.wallRoom.roomLevelId];
    ensureDefined(level);

    const levelAttributes = this.levelAttributes[level.id];
    ensureDefined(levelAttributes);

    return datumAbstraction.resolveVerticalDatumRelativeToLevel({
      level,
      levelAttributes,
      wallSide,
      extraOffset,
    });
  }

  public resolveVerticalDatumRelativeToWallSide(
    datumUsage: DatumUsage<DatumVerticalUsageType>,
    wallSide: WallSideDescriptor,
    wallSideVertialDatumUsage: "WALL_TOP" | "WALL_BOTTOM",
    componentInstanceChain?: string[],
  ): LevelInches {
    const datumAbstraction = this.getWallVerticalDatumAbstraction(
      datumUsage,
      wallSideVertialDatumUsage,
      componentInstanceChain,
    );
    return this.resolveWallVerticalDatumAbstractionRelativeToWallSide(datumAbstraction, wallSide, datumUsage.offset);
  }
}
